import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiClient } from '../../lib/apiClient';
import { formatBytes } from '../../tools/transfer/api';
import TransferSlidesManager from './TransferSlidesManager';
import TransferPagesManager from './TransferPagesManager';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, Copy, Plus, Trash2 } from 'lucide-react';
import { useSiteFeatureFlags } from '../../hooks/useSiteFeatureFlags';

const input = 'w-full border border-gray-300 rounded-lg px-3 min-h-[40px] bg-white';
const label = 'block text-sm font-medium text-gray-700';

const apiError = (err, fallback) => {
  const d = err.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const AccountRow = ({ account, onSaved }) => {
  const [plan, setPlan] = useState(account.transfer_plan);
  const [days, setDays] = useState(account.transfer_retention_days ?? '');
  const [quota, setQuota] = useState(account.transfer_daily_quota_mb ?? '');
  const [forever, setForever] = useState(!!account.transfer_keep_forever);

  const save = async () => {
    try {
      await apiClient.put(`/transfers/admin/accounts/${account.id}`, {
        transfer_plan: plan,
        transfer_retention_days: plan === 'paid' && days !== '' ? Number(days) : null,
        transfer_daily_quota_mb: plan === 'paid' && quota !== '' ? Number(quota) : null,
        transfer_keep_forever: plan === 'paid' && forever,
      });
      toast.success(`Saved ${account.email}`);
      onSaved();
    } catch (err) {
      toast.error(apiError(err, 'Could not save'));
    }
  };

  return (
    <tr className="border-t" data-testid={`transfer-account-${account.id}`}>
      <td className="p-2"><div className="font-medium">{account.name}</div><div className="text-xs text-gray-500">{account.email}</div></td>
      <td className="p-2">
        <select value={plan} onChange={(e) => setPlan(e.target.value)} className={input}>
          <option value="free">Free</option>
          <option value="paid">Paid</option>
        </select>
      </td>
      <td className="p-2"><input type="number" min="1" disabled={plan !== 'paid'} value={days} placeholder="default"
        onChange={(e) => setDays(e.target.value)} className={input} /></td>
      <td className="p-2"><input type="number" min="1" disabled={plan !== 'paid'} value={quota} placeholder="default"
        onChange={(e) => setQuota(e.target.value)} className={input} /></td>
      <td className="p-2">
        <label className="inline-flex items-center gap-2 text-xs whitespace-nowrap">
          <input type="checkbox" disabled={plan !== 'paid'} checked={forever} onChange={(e) => setForever(e.target.checked)} data-testid={`transfer-forever-${account.id}`} />
          Keep forever
        </label>
      </td>
      <td className="p-2"><button type="button" onClick={save} className="px-3 min-h-[40px] rounded-lg bg-blue-600 text-white">Save</button></td>
    </tr>
  );
};

// Where a transfer came from: this site, or a client's site through the embed widget.
const sourceName = (x) => {
  if (x.source !== 'embed') return 'This site';
  const origin = x.source_origin ?? x.origin;
  if (!origin) return 'Embed (unknown site)';
  try { return `Embed · ${new URL(origin).host}`; } catch { return 'Embed'; }
};
const sourceTone = (x) => (x.source === 'embed' ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-gray-50 text-gray-600 border-gray-200');

const DevTransferSettings = () => {
  const { transfer_embed_enabled } = useSiteFeatureFlags();
  const [s, setS] = useState(null);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [tx, setTx] = useState({ transfers: [], sources: [] });
  const [srcFilter, setSrcFilter] = useState('');

  const loadStats = useCallback(() => {
    apiClient.get('/transfers/admin/stats').then((r) => setStats(r.data)).catch(() => {});
  }, []);
  const loadAccounts = useCallback(() => {
    apiClient.get('/transfers/admin/accounts', { params: { q } }).then((r) => setAccounts(r.data)).catch(() => {});
  }, [q]);

  const loadTransfers = useCallback(() => {
    apiClient.get('/transfers/admin/transfers', { params: { source: srcFilter } }).then((r) => setTx(r.data)).catch(() => {});
  }, [srcFilter]);
  useEffect(() => { loadTransfers(); }, [loadTransfers]);

  useEffect(() => {
    apiClient.get('/transfers/admin/settings').then((r) => setS(r.data))
      .catch(() => toast.error('Could not load transfer settings'));
    loadStats();
  }, [loadStats]);
  useEffect(loadAccounts, [loadAccounts]);

  if (!s) return <p className="p-6 text-gray-500">Loading…</p>;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const num = (k) => (e) => setS({ ...s, [k]: Number(e.target.value) });

  const save = async () => {
    try {
      await apiClient.put('/transfers/admin/settings', {
        ...s,
        free_expiry_days: Array.isArray(s.free_expiry_days)
          ? s.free_expiry_days
          : String(s.free_expiry_days).split(/[,\s]+/).filter(Boolean).map(Number),
        paid_expiry_options: Array.isArray(s.paid_expiry_options)
          ? s.paid_expiry_options
          : String(s.paid_expiry_options).split(/[,\s]+/).filter(Boolean).map(Number),
        embed_origins: Array.isArray(s.embed_origins)
          ? s.embed_origins
          : String(s.embed_origins).split(/[\s,]+/).filter(Boolean),
      });
      toast.success('Transfer settings saved');
      const r = await apiClient.get('/transfers/admin/settings');
      setS(r.data);
    } catch (err) {
      toast.error(apiError(err, 'Could not save settings'));
    }
  };

  const sweep = async () => {
    const r = await apiClient.post('/transfers/admin/sweep');
    toast.success(`Removed ${r.data.removed} expired transfer(s)`);
    loadStats();
  };

  const listText = (v) => (Array.isArray(v) ? v.join(', ') : v);
  const embedUrl = `${window.location.origin}/embed/transfer`;
  const iframeCode = `<iframe src="${embedUrl}" title="Send files" style="width:100%;max-width:408px;height:540px;border:0" allow="clipboard-write"></iframe>`;
  const scriptCode = `<div id="a2g-transfer"></div>\n<script src="${window.location.origin}/api/transfers/embed.js" async></script>`;
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch {
      toast.error('Copy failed: select the code and copy it by hand');
    }
  };

  return (
    <div className="space-y-8 max-w-4xl text-gray-900" data-testid="dev-transfer-settings">
      <p className="text-sm text-gray-600">
        Turn the tool on under <strong>Feature Flags → File Transfer</strong>. When it is off, none of this exists publicly.
      </p>

      <section className="grid sm:grid-cols-4 gap-3">
        {['active', 'uploading'].map((k) => (
          <div key={k} className="bg-white border rounded-lg p-4">
            <div className="text-xs uppercase text-gray-500">{k}</div>
            <div className="text-2xl font-bold">{stats[k]?.count ?? 0}</div>
            <div className="text-xs text-gray-500">{formatBytes(stats[k]?.bytes ?? 0)}</div>
          </div>
        ))}
        <button type="button" onClick={sweep} className="bg-white border rounded-lg p-4 text-left hover:bg-gray-50">
          <div className="font-medium">Clean up now</div>
          <div className="text-xs text-gray-500">Delete expired transfers (also runs hourly)</div>
        </button>
      </section>

      <section className="bg-white border rounded-lg p-5 space-y-3" data-testid="transfer-recent">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold">Recent transfers</h2>
          <select className="border border-gray-300 rounded-lg px-3 min-h-[36px] bg-white text-sm" value={srcFilter}
            onChange={(e) => setSrcFilter(e.target.value)} data-testid="transfer-source-filter">
            <option value="">All sources</option>
            <option value="site">This site</option>
            <option value="embed">All embeds</option>
            {tx.sources.filter((x) => x.origin).map((x) => (
              <option key={x.origin} value={`origin:${x.origin}`}>{sourceName(x)}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="transfer-source-counts">
          {tx.sources.map((x) => (
            <span key={`${x.source}-${x.origin}`} className={`text-xs px-2 py-1 rounded-full border ${sourceTone(x)}`}>
              {sourceName(x)} · {x.count} · {formatBytes(x.bytes)}
            </span>
          ))}
        </div>
        {tx.transfers.length === 0 ? (
          <p className="text-sm text-gray-500">No transfers yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500">
                <th className="py-1 pr-3">When</th><th className="pr-3">Source</th><th className="pr-3">Sender</th>
                <th className="pr-3">Files</th><th className="pr-3">Size</th><th>Status</th>
              </tr></thead>
              <tbody>
                {tx.transfers.map((t) => (
                  <tr key={t.id} className="border-t" data-testid="transfer-row">
                    <td className="py-2 pr-3 whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
                    <td className="pr-3"><span className={`text-xs px-2 py-1 rounded-full border whitespace-nowrap ${sourceTone(t)}`} data-testid="transfer-row-source">{sourceName(t)}</span></td>
                    <td className="pr-3 break-all">{t.sender || '-'}</td>
                    <td className="pr-3">{t.file_count}{t.title ? ` · ${t.title}` : ''}</td>
                    <td className="pr-3 whitespace-nowrap">{formatBytes(t.total_bytes)}</td>
                    <td>{t.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-white border rounded-lg p-5 space-y-4">
        <h2 className="font-semibold">Limits</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className={label}>Free daily quota (MB per sender, 24h)
            <input type="number" min="1" className={input} value={s.free_daily_quota_mb} onChange={num('free_daily_quota_mb')} /></label>
          <label className={label}>Free expiry choices (days, comma separated)
            <input className={input} value={listText(s.free_expiry_days)} onChange={set('free_expiry_days')} /></label>
          <label className={label}>Max files per transfer
            <input type="number" min="1" className={input} value={s.max_files} onChange={num('max_files')} /></label>
          <label className={label}>Who can send
            <select className={input} value={s.who_can_send} onChange={set('who_can_send')}>
              <option value="anyone">Anyone (rate limited)</option>
              <option value="login">Logged-in users only</option>
            </select></label>
          <label className={label}>Paid default retention (days)
            <input type="number" min="1" className={input} value={s.paid_default_retention_days} onChange={num('paid_default_retention_days')} /></label>
          <label className={label}>Paid default daily quota (MB)
            <input type="number" min="1" className={input} value={s.paid_daily_quota_mb} onChange={num('paid_daily_quota_mb')} /></label>
          <label className={label}>Free: max people to email per transfer
            <input type="number" min="1" max="50" className={input} value={s.free_max_recipients} onChange={num('free_max_recipients')} /></label>
          <label className={label}>Paid: max people to email per transfer
            <input type="number" min="1" max="50" className={input} value={s.paid_max_recipients} onChange={num('paid_max_recipients')} /></label>
          <label className={`${label} sm:col-span-2`}>Paid expiry steps offered (days, comma separated; "Keep forever" is set per account)
            <input className={input} value={listText(s.paid_expiry_options)} onChange={set('paid_expiry_options')} /></label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={s.allow_password} onChange={set('allow_password')} /> Allow password protection</label>
        </div>
      </section>

      <section className="bg-white border rounded-lg p-5 space-y-4">
        <h2 className="font-semibold">Branding</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className={label}>Name<input className={input} maxLength={80} value={s.brand_name} onChange={set('brand_name')} /></label>
          <label className={label}>Headline (big text on the landing page)<input className={input} maxLength={120} value={s.brand_headline} onChange={set('brand_headline')} /></label>
          <label className={label}>Seconds per slide
            <input type="number" min="3" max="60" className={input} value={s.slide_seconds} onChange={num('slide_seconds')} /></label>
          <label className={label}>Tagline<input className={input} maxLength={160} value={s.brand_tagline} onChange={set('brand_tagline')} /></label>
          <label className={label}>Logo URL<input className={input} maxLength={500} value={s.brand_logo_url} onChange={set('brand_logo_url')} /></label>
          <label className={label}>Accent colour
            <input type="color" className={`${input} p-1`} value={s.brand_accent} onChange={set('brand_accent')} /></label>
          <label className={`${label} sm:col-span-2`}>Footer text<input className={input} maxLength={300} value={s.footer_text} onChange={set('footer_text')} /></label>
        </div>
      </section>

      <section className="bg-white border rounded-lg p-5 space-y-3">
        <h2 className="font-semibold">Landing page slides</h2>
        <p className="text-sm text-gray-600">Client advertising backgrounds behind the upload box on /transfer.</p>
        <TransferSlidesManager />
      </section>

      <section className="bg-white border rounded-lg p-5 space-y-3">
        <h2 className="font-semibold">Pages (Transfer Site template)</h2>
        <p className="text-sm text-gray-600">The individual pages of the Transfer Site template, shown at <code>/p/your-address</code>. About, Pricing, FAQ and Support start with true text whose numbers follow the limits above.</p>
        <TransferPagesManager />
      </section>

      <section className="bg-white border rounded-lg p-5 space-y-3" data-testid="transfer-menu-editor">
        <h2 className="font-semibold">Site menu (Transfer Site template)</h2>
        <p className="text-sm text-gray-600">The links in the top menu when the template is on. Log in / Sign up are always added. Use <code>/p/your-address</code> for a page, or a full https:// link. Saved with &ldquo;Save settings&rdquo; below.</p>
        {(Array.isArray(s.menu_items) ? s.menu_items : []).map((m, i, all) => (
          <div key={i} className="flex flex-wrap items-center gap-2" data-testid="transfer-menu-row">
            <input className={`${input} sm:w-48`} placeholder="Label" maxLength={40} value={m.label}
              onChange={(e) => setS({ ...s, menu_items: all.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
            <input className={`${input} flex-1 min-w-[10rem]`} placeholder="/p/about or https://…" list="transfer-menu-links" maxLength={300} value={m.href}
              onChange={(e) => setS({ ...s, menu_items: all.map((x, j) => (j === i ? { ...x, href: e.target.value } : x)) })} />
            <button type="button" aria-label="Move up" disabled={i === 0} className="w-10 h-10 inline-flex items-center justify-center rounded border disabled:opacity-30"
              onClick={() => { const n = [...all]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; setS({ ...s, menu_items: n }); }}><ArrowUp className="w-4 h-4" /></button>
            <button type="button" aria-label="Move down" disabled={i === all.length - 1} className="w-10 h-10 inline-flex items-center justify-center rounded border disabled:opacity-30"
              onClick={() => { const n = [...all]; [n[i + 1], n[i]] = [n[i], n[i + 1]]; setS({ ...s, menu_items: n }); }}><ArrowDown className="w-4 h-4" /></button>
            <button type="button" aria-label="Remove link" className="w-10 h-10 inline-flex items-center justify-center rounded border text-red-600"
              onClick={() => setS({ ...s, menu_items: all.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <datalist id="transfer-menu-links">
          {['/', '/p/about', '/p/pricing', '/p/faq', '/p/support'].map((v) => <option key={v} value={v} />)}
        </datalist>
        <button type="button" disabled={(s.menu_items || []).length >= 12} className="px-4 min-h-[40px] rounded-lg border border-gray-300 inline-flex items-center gap-2 text-sm disabled:opacity-40"
          onClick={() => setS({ ...s, menu_items: [...(s.menu_items || []), { label: '', href: '' }] })} data-testid="transfer-menu-add"><Plus className="w-4 h-4" /> Add link</button>
      </section>

      {!transfer_embed_enabled ? (
        <section className="bg-white border rounded-lg p-5 space-y-2" data-testid="transfer-embed-off">
          <h2 className="font-semibold">Embed on a client&apos;s site</h2>
          <p className="text-sm text-gray-600">
            This is a separate module. Turn on <strong>Transfer Embed Widget</strong> under{' '}
            <Link to="/dev/settings/modules" className="text-blue-600 underline">Modules &amp; Tools</Link> to get the copy-paste code.
          </p>
        </section>
      ) : (
        <section className="bg-white border rounded-lg p-5 space-y-4" data-testid="transfer-embed-on">
          <h2 className="font-semibold">Embed on a client&apos;s site</h2>
          <p className="text-sm text-gray-600">
            List the exact origins allowed to show the tool (one per line, e.g. <code>https://client.com</code>) and save.
            Anything not listed is blocked. Files, links and emails all stay on this site.
          </p>
          <textarea rows={3} className={`${input} py-2`} value={Array.isArray(s.embed_origins) ? s.embed_origins.join('\n') : s.embed_origins}
            onChange={(e) => setS({ ...s, embed_origins: e.target.value })} data-testid="transfer-embed-origins" />

          {[
            { key: 'script', title: 'Option 1 - one line of JavaScript (sizes itself)', code: scriptCode,
              hint: 'Paste where the box should appear. It is the same size as the box on the landing page (408px, shrinks on phones). Optional on the script tag: data-width="100%" to fill the container.' },
            { key: 'iframe', title: 'Option 2 - plain HTML iframe (fixed height)', code: iframeCode, hint: 'Works anywhere HTML is allowed.' },
          ].map((o) => (
            <div key={o.key} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-gray-700">{o.title}</p>
                <button type="button" onClick={() => copy(o.code)} className="inline-flex items-center gap-1 px-3 min-h-[36px] rounded-lg border text-sm"
                  data-testid={`transfer-embed-copy-${o.key}`}><Copy className="w-4 h-4" /> Copy</button>
              </div>
              <pre className="bg-gray-100 rounded p-3 text-xs overflow-x-auto select-all" data-testid={`transfer-embed-code-${o.key}`}>{o.code}</pre>
              <p className="text-xs text-gray-500">{o.hint}</p>
            </div>
          ))}

          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Live preview</p>
            <iframe src={embedUrl} title="Embed preview" className="w-full max-w-[408px] border rounded-lg" style={{ height: 560 }} data-testid="transfer-embed-preview" />
          </div>
        </section>
      )}

      <button type="button" onClick={save} className="px-6 min-h-[44px] rounded-lg bg-blue-600 text-white font-medium" data-testid="transfer-settings-save">
        Save settings
      </button>

      <section className="bg-white border rounded-lg p-5 space-y-3">
        <h2 className="font-semibold">Accounts (free / paid)</h2>
        <p className="text-sm text-gray-600">
          Paid is set here by hand until billing exists. Leave retention / quota blank to use the paid defaults above.
          Search by name or email; with no search, paid accounts are listed.
        </p>
        <input className={input} placeholder="Search accounts…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-500"><th className="p-2">Account</th><th className="p-2">Plan</th><th className="p-2">Retention (days)</th><th className="p-2">Daily quota (MB)</th><th className="p-2">Unlimited retention</th><th /></tr></thead>
            <tbody>{accounts.map((a) => <AccountRow key={a.id} account={a} onSaved={loadAccounts} />)}</tbody>
          </table>
          {accounts.length === 0 && <p className="text-sm text-gray-500 p-2">No accounts to show.</p>}
        </div>
      </section>
    </div>
  );
};

export default DevTransferSettings;
